import { useState } from "react";
import {
  HiOutlineBell, HiOutlineEnvelope, HiOutlineHeart,
  HiOutlineClipboardDocumentList, HiOutlineMagnifyingGlass,
  HiOutlineXMark, HiOutlineChevronDown, HiOutlineChevronLeft,
  HiOutlineChevronRight, HiOutlineEllipsisHorizontal,
  HiOutlineCheckCircle, HiOutlineTrash, HiOutlineArrowRight,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { notificationsAPI } from "../services/api";
import toast from "react-hot-toast";
import { useEffect, useCallback } from "react";
import "./AdminNotifications.css";

const TYPE_META = {
  Application: { Icon: HiOutlineClipboardDocumentList, color:"#e05a2b", bg:"rgba(var(--admin-rust-rgb), 0.1)",  badgeCls:"ann__type--app"  },
  Subscriber:  { Icon: HiOutlineEnvelope,              color:"#16a34a", bg:"rgba(22,163,74,0.1)",  badgeCls:"ann__type--sub"  },
  Donation:    { Icon: HiOutlineHeart,                 color:"#7c3aed", bg:"rgba(124,58,237,0.1)", badgeCls:"ann__type--don"  },
};

// NOTIF_STATS built dynamically from summary in component

function DetailPanel({ notif, onClose, onMarkRead, onDelete, onViewApp }) {
  const meta = TYPE_META[notif.type] || TYPE_META.Application;
  return (
    <div className="ann__detail">
      <div className="ann__detail-head">
        <h3 className="ann__detail-title">Notification Details</h3>
        <button className="ann__detail-close" onClick={onClose}><HiOutlineXMark /></button>
      </div>

      <div className="ann__detail-body">
        <div className="ann__detail-icon-row">
          <div className="ann__detail-type-icon" style={{ background:meta.bg }}>
            <meta.Icon style={{ color:meta.color, fontSize:"1.4rem" }} />
          </div>
          <div>
            <h4 className="ann__detail-notif-title">{notif.title}</h4>
            <div className="ann__detail-read-row">
              <span className={`ann__read-dot ${notif.is_read ? "ann__read-dot--read" : "ann__read-dot--unread"}`} />
              <span className="ann__read-label">{notif.read ? "Read" : "Unread"}</span>
            </div>
            <p className="ann__detail-date">{notif.created_at ? new Date(notif.created_at).toLocaleString("en-US",{year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit"}) : notif.time || ""}</p>
          </div>
        </div>

        <div className="ann__detail-section">
          <p className="ann__detail-section-title">Message</p>
          <p className="ann__detail-message">
            {notif.type === "Application"
              ? `${notif.message.replace("submitted a new application.", "has submitted a new application for the retreat program.")}`
              : notif.desc}
          </p>
        </div>

        {notif.detail && (
          <div className="ann__detail-section">
            <p className="ann__detail-section-title">Details</p>
            <div className="ann__detail-rows">
              <div className="ann__detail-row"><span>Applicant Name</span><span>{notif.detail.name}</span></div>
              <div className="ann__detail-row"><span>Category</span><span>{notif.detail.category}</span></div>
              <div className="ann__detail-row"><span>Submitted On</span><span>{notif.detail.date}</span></div>
              <div className="ann__detail-row"><span>Application ID</span><span>{notif.detail.appId}</span></div>
            </div>
          </div>
        )}

        <div className="ann__detail-section ann__detail-actions">
          <p className="ann__detail-section-title">Actions</p>
          {notif.type === "Application" && notif.reference_id && (
            <button type="button" className="a-btn a-btn--rust" style={{ width:"100%", marginBottom:"0.5rem" }}
              onClick={() => onViewApp(notif.reference_id)}>
              View Application <HiOutlineArrowRight />
            </button>
          )}
          <button type="button"
            className={`a-btn ${notif.is_read ? "a-btn--outline" : "a-btn--outline"} ann__action-btn`}
            onClick={() => onMarkRead(notif.id)}>
            <HiOutlineCheckCircle /> {notif.is_read ? "Already Read" : "Mark as Read"}
          </button>
          <button type="button" className="ann__delete-btn" onClick={() => { onDelete(notif.id); onClose(); }}>
            <HiOutlineTrash /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminNotifications() {
  const [items, setItems]         = useState([]);
  const [summary, setSummary] = useState({total:0,unread:0,applications:0,subscribers:0,donations:0});
  // eslint-disable-next-line no-unused-vars
  const [_loading, setLoading] = useState(false);
  const [search, setSearch]       = useState("");
  const [typeFilter, setType]     = useState("All Types");
  const [statusFilter, setStatusF]= useState("All Status");
  const [selected, setSelected]   = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const fetchNotifs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "All Types") params.type = typeFilter;
      if (statusFilter === "Unread")  params.is_read = "0";
      if (statusFilter === "Read")    params.is_read = "1";
      if (search)                     params.search = search;
      const res = await notificationsAPI.list(params);
      setItems(res.data || []);
      setSummary(res.summary || {});
    } catch (err) { console.error(err.message); }
    finally { setLoading(false); }
  }, [typeFilter, statusFilter, search]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  const markRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setSelected(prev => prev?.id === id ? { ...prev, is_read: true } : prev);
      fetchNotifs();
      toast.success("Notification marked as read.");
    } catch (err) { toast.error("Failed to mark as read."); }
  };
  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      fetchNotifs();
      toast.success("All notifications marked as read.");
    } catch (err) { console.error(err); }
  };
  const deleteItem = async (id) => {
    const tid = toast.loading("Deleting…");
    try {
      await notificationsAPI.delete(id); fetchNotifs(); setSelected(null);
      toast.success("Notification deleted.", { id: tid });
    } catch (err) { toast.error("Failed to delete.", { id: tid }); }
  };

  // Client-side tab filter (server already filtered by type/status)
  const filtered = items.filter(n => {
    if (activeTab === "unread"     && n.is_read)                    return false;
    if (activeTab === "Application"&& n.type!=="Application")       return false;
    if (activeTab === "Subscriber" && n.type!=="Subscriber")        return false;
    if (activeTab === "Donation"   && n.type!=="Donation")          return false;
    return true;
  });


  return (
    <AdminLayout pageTitle="Notifications" pageSubtitle="Stay up to date with important activity across Forge." permission="notifications.view">

      {/* Stat cards — values from live summary */}
      <div className="ann__stats-grid">
        {[
          { label:"All Notifications", val:summary.total         ?? items.length, sub:"View all",    Icon:HiOutlineBell,                  color:"#e05a2b", bg:"rgba(var(--admin-rust-rgb), 0.1)",  link:"all"         },
          { label:"Unread",            val:summary.unread        ?? items.filter(n=>!n.is_read).length, sub:"View unread", Icon:HiOutlineBell, color:"#16a34a", bg:"rgba(22,163,74,0.1)", link:"unread", green:true },
          { label:"Applications",      val:summary.applications  ?? 0, sub:"View all",    Icon:HiOutlineClipboardDocumentList, color:"#7c3aed", bg:"rgba(124,58,237,0.1)", link:"Application" },
          { label:"Subscribers",       val:summary.subscribers   ?? 0, sub:"View all",    Icon:HiOutlineEnvelope,              color:"#d97706", bg:"rgba(217,119,6,0.1)",  link:"Subscriber"  },
          { label:"Donations",         val:summary.donations     ?? 0, sub:"View all",    Icon:HiOutlineHeart,                 color:"#e05a2b", bg:"rgba(var(--admin-rust-rgb), 0.1)",  link:"Donation"    },
        ].map(s => (
          <div key={s.label} className={`acard ann__stat-card ${s.green ? "ann__stat-card--green" : ""}`}>
            <div className="ann__stat-icon-row">
              <div className="ann__stat-icon" style={{ background:s.bg }}>
                {s.green ? <span className="ann__green-dot" /> : <s.Icon style={{ color:s.color, fontSize:"1.1rem" }} />}
              </div>
            </div>
            <p className="ann__stat-label">{s.label}</p>
            <p className={`ann__stat-value ${s.green?"ann__stat-value--green":""}`}>{s.val}</p>
            <button type="button" className="ann__stat-link" onClick={() => setActiveTab(s.link)}>
              {s.sub}
            </button>
          </div>
        ))}
      </div>

      <div className={`ann__layout ${selected ? "ann__layout--detail" : ""}`}>
        <div className="ann__list-panel">
          {/* Filters row */}
          <div className="ann__filters">
            <div className="asu__search-wrap">
              <HiOutlineMagnifyingGlass className="asu__search-icon" />
              <input type="text" className="asu__search" placeholder="Search notifications..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={typeFilter} onChange={e => setType(e.target.value)}>
                {["All Types","Application","Subscriber","Donation"].map(t => <option key={t}>{t}</option>)}
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>
            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={statusFilter} onChange={e => setStatusF(e.target.value)}>
                {["All Status","Unread","Read"].map(s => <option key={s}>{s}</option>)}
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>
            <div className="ann__filters-right">
              <button type="button" className="a-btn a-btn--outline" onClick={markAllRead}>
                <HiOutlineCheckCircle /> Mark all as read
              </button>
            </div>
          </div>

          {/* List */}
          <div className="acard ann__list-card">
            {filtered.length === 0 && (
              <p className="ann__empty">No notifications found.</p>
            )}
            {filtered.map(n => {
              const meta = TYPE_META[n.type] || TYPE_META.Application;
              return (
                <div key={n.id}
                  className={`ann__item ${!n.is_read ? "ann__item--unread" : ""} ${selected?.id===n.id ? "ann__item--selected" : ""}`}
                  onClick={() => { setSelected(n); markRead(n.id); }}>
                  {/* Unread dot */}
                  <span className={`ann__unread-dot ${!n.is_read ? "ann__unread-dot--active" : ""}`} aria-hidden="true" />

                  {/* Icon */}
                  <div className="ann__item-icon" style={{ background:meta.bg }}>
                    <meta.Icon style={{ color:meta.color, fontSize:"1rem" }} />
                  </div>

                  {/* Content */}
                  <div className="ann__item-body">
                    <p className="ann__item-title">{n.title}</p>
                    <p className="ann__item-desc">{n.message}</p>
                  </div>

                  {/* Type badge + time + more */}
                  <div className="ann__item-right">
                    <span className={`ann__type-badge ${meta.badgeCls}`}>{n.type}</span>
                    <span className="ann__item-time">{n.created_at ? new Date(n.created_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : (n.time || "")}</span>
                  </div>

                  <button type="button" className="ann__item-more a-btn a-btn--ghost"
                    onClick={e => { e.stopPropagation(); }}>
                    <HiOutlineEllipsisHorizontal />
                  </button>
                </div>
              );
            })}

            {/* Pagination */}
            <div className="aap__pagination">
              <p className="aap__pagination-info">Showing 1 to {filtered.length} of 28 notifications</p>
              <div className="aap__pagination-pages">
                <button type="button" className="aap__page-btn" disabled><HiOutlineChevronLeft /></button>
                {[1,2,3,"...",4].map((p,i) => (
                  <button key={i} type="button" className={`aap__page-btn ${p===1?"aap__page-btn--active":""}`}>{p}</button>
                ))}
                <button type="button" className="aap__page-btn"><HiOutlineChevronRight /></button>
              </div>
              <div className="aap__select-wrap">
                <select className="aap__page-size"><option>10 / page</option><option>20 / page</option></select>
                <HiOutlineChevronDown className="aap__select-icon" />
              </div>
            </div>
          </div>
        </div>

        {selected && (
          <DetailPanel
            notif={selected}
            onClose={() => setSelected(null)}
            onMarkRead={markRead}
            onDelete={deleteItem}
            onViewApp={(appId) => { window.location.href = `/admin/applications`; }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
